import React, { useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";

export function RatesTab({ rates, reload }) {
  const [form, setForm] = useState({
    service_fee_percent: rates.current.service_fee_percent,
    gateway_fee_percent: rates.current.gateway_fee_percent,
    gateway_fee_fixed_usd: rates.current.gateway_fee_fixed_usd,
  });
  const [busy, setBusy] = useState(false);
  const defaults = rates.defaults;
  const isOverridden =
    form.service_fee_percent !== defaults.service_fee_percent ||
    form.gateway_fee_percent !== defaults.gateway_fee_percent ||
    form.gateway_fee_fixed_usd !== defaults.gateway_fee_fixed_usd;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v === "" ? "" : Number(v) }));

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/admin/rates", {
        service_fee_percent: Number(form.service_fee_percent),
        gateway_fee_percent: Number(form.gateway_fee_percent),
        gateway_fee_fixed_usd: Number(form.gateway_fee_fixed_usd),
      });
      toast.success("Rates updated");
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save rates");
    } finally { setBusy(false); }
  };

  const reset = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/rates/reset");
      toast.success("Reverted to defaults");
      setForm({ ...r.data.current });
      reload();
    } catch {
      toast.error("Reset failed");
    } finally { setBusy(false); }
  };

  const preview = (() => {
    const base = 100;
    const svc = +(base * form.service_fee_percent / 100).toFixed(2) || 0;
    const gwpct = base * form.gateway_fee_percent / 100;
    const gw = +(gwpct + Number(form.gateway_fee_fixed_usd || 0)).toFixed(2) || 0;
    const total = +(base + svc + gw).toFixed(2);
    return { base, svc, gw, total };
  })();

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-8" data-testid="rates-panel">
      <div className="space-y-6 bg-white border border-[#E5E0D8] rounded-2xl p-8">
        <div>
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">Pricing</p>
          <h3 className="serif text-3xl mt-1">Fees &amp; rates</h3>
          <p className="text-sm text-[#4A5C50] mt-1">
            Applied to every new card issuance, Stripe top-up, and internal "Load funds" flow.
            Defaults come from environment; saved overrides take effect immediately.
          </p>
        </div>
        <RateField
          label="Service fee" suffix="%"
          hint={`Default ${defaults.service_fee_percent}% · Charged on top of the card amount`}
          value={form.service_fee_percent}
          onChange={(v) => set("service_fee_percent", v)}
          testId="rate-service-percent"
        />
        <RateField
          label="Gateway fee (percent)" suffix="%"
          hint={`Default ${defaults.gateway_fee_percent}% · Stripe processing percentage`}
          value={form.gateway_fee_percent}
          onChange={(v) => set("gateway_fee_percent", v)}
          testId="rate-gateway-percent"
        />
        <RateField
          label="Gateway fee (fixed)" suffix="USD"
          hint={`Default $${defaults.gateway_fee_fixed_usd.toFixed(2)} · Fixed per-transaction Stripe fee`}
          value={form.gateway_fee_fixed_usd}
          onChange={(v) => set("gateway_fee_fixed_usd", v)}
          testId="rate-gateway-fixed"
        />

        <div className="flex flex-wrap gap-3 pt-4 border-t border-[#E5E0D8]">
          <Button onClick={save} disabled={busy}
            className="rounded-xl bg-[#184A30] hover:bg-[#0F3320] text-white"
            data-testid="rates-save-btn">
            {busy ? "Saving…" : "Save rates"}
          </Button>
          <Button onClick={reset} disabled={busy || !isOverridden} variant="outline"
            className="rounded-xl border-[#E5E0D8] text-[#4A5C50] hover:bg-[#FBF9F6]"
            data-testid="rates-reset-btn"
            title={isOverridden ? "Revert to environment defaults" : "Already at defaults"}>
            Revert to defaults
          </Button>
          {isOverridden && (
            <span className="text-[11px] uppercase tracking-widest px-2 py-1 rounded-full bg-[#CBA35C]/20 text-[#8C6B21] self-center">
              Custom override active
            </span>
          )}
        </div>
      </div>

      <div className="bg-[#112318] text-[#FBF9F6] rounded-2xl p-8 space-y-4" data-testid="rates-preview">
        <p className="text-[10px] tracking-[0.3em] uppercase opacity-60">Live preview · $100 load</p>
        <div className="space-y-3 mt-4">
          <PreviewRow label="Card amount" value={`$${preview.base.toFixed(2)}`} />
          <PreviewRow label={`Service fee · ${Number(form.service_fee_percent || 0).toFixed(2)}%`} value={`$${preview.svc.toFixed(2)}`} />
          <PreviewRow
            label={`Gateway · ${Number(form.gateway_fee_percent || 0).toFixed(2)}% + $${Number(form.gateway_fee_fixed_usd || 0).toFixed(2)}`}
            value={`$${preview.gw.toFixed(2)}`}
          />
        </div>
        <div className="border-t border-white/10 pt-4 flex items-baseline justify-between">
          <p className="text-[10px] tracking-[0.3em] uppercase opacity-60">Customer pays</p>
          <p className="serif text-4xl" data-testid="rates-preview-total">${preview.total.toFixed(2)}</p>
        </div>
        <p className="text-[11px] opacity-50 pt-4">
          Receiver still gets the full <span className="mono text-[#8BA888]">$100.00</span> on the card.
        </p>
      </div>
    </div>
  );
}

function RateField({ label, suffix, hint, value, onChange, testId }) {
  return (
    <div>
      <label className="text-[10px] tracking-[0.3em] uppercase text-[#4A5C50]">{label}</label>
      <div className="flex items-center gap-3 mt-1 border-b border-[#E5E0D8] focus-within:border-[#184A30] transition">
        <input type="number" step="0.01" min="0" value={value}
          onChange={(e) => onChange(e.target.value)}
          className="flex-1 bg-transparent py-2 mono text-2xl text-[#112318] focus:outline-none"
          data-testid={testId} />
        <span className="text-xs mono text-[#4A5C50]">{suffix}</span>
      </div>
      <p className="text-[11px] text-[#4A5C50] mt-2">{hint}</p>
    </div>
  );
}

function PreviewRow({ label, value }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="opacity-70">{label}</span>
      <span className="mono">{value}</span>
    </div>
  );
}
